(function () {
  const MINI_CART_SELECTOR = '[data-mini-cart]';
  const SELECTOR_CONTAINER = '[data-mini-cart-gwp-selector]';
  const CONFIG_CONTAINER = '[data-mini-cart-gwp-config]';
  const TIER_SOURCE = '[data-mini-cart-gwp-tier]';
  const OPTION_SOURCE = '[data-mini-cart-gwp-option]';
  const MINI_CART_VIEW_URL = '/search?view=mini-cart&q=cart';
  const GWP_PROPERTY = '_gwp_gift';
  const GWP_TIER_PROPERTY = '_free_gift_tier';
  const REWARD_MILESTONE_PROPERTY = '_reward_milestone_key';

  let isReconciling = false;
  let reconcileTimer = null;
  let allowNextMiniCartTrigger = false;

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    const template = window.moneyFormat || '${{amount}}';
    const formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return template
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, formatted)
      .replace(/\{\{\s*amount\s*\}\}/g, formatted)
      .replace(/([.,]00)(?!\d)/, '');
  }

  function getMiniCartRoot() {
    return document.querySelector(MINI_CART_SELECTOR);
  }

  function parseConfig(root) {
    const configRoot = root?.querySelector(CONFIG_CONTAINER);
    if (!configRoot) return [];

    return Array.from(configRoot.querySelectorAll(TIER_SOURCE))
      .map((tierNode) => {
        const tier = Number.parseInt(tierNode.dataset.tier || '', 10);
        const thresholdCents = Number.parseInt(tierNode.dataset.thresholdCents || '', 10);
        const allowMultiple = tierNode.dataset.allowMultiple === 'true';
        const label = tierNode.dataset.label || 'Free Gift';
        const options = Array.from(tierNode.querySelectorAll(OPTION_SOURCE))
          .map((optionNode) => {
            const variantId = Number.parseInt(optionNode.dataset.variantId || '', 10);
            const productId = Number.parseInt(optionNode.dataset.productId || '', 10);
            if (!variantId || !productId) return null;

            return {
              productId,
              variantId,
              title: optionNode.dataset.title || 'Gift',
              imageUrl: optionNode.dataset.imageUrl || '',
              isDefault: optionNode.dataset.isDefault === 'true',
              isAvailable: optionNode.dataset.isAvailable !== 'false',
            };
          })
          .filter(Boolean);

        if (!tier || !thresholdCents || options.length === 0) return null;

        return {
          tier,
          thresholdCents,
          allowMultiple,
          label,
          options,
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.thresholdCents - right.thresholdCents);
  }

  function parseRewardMilestones(root) {
    return Array.from(root.querySelectorAll('[data-mini-cart-reward-source-item]'))
      .map((node) => {
        const thresholdCents = Number.parseInt(node.dataset.thresholdCents || '', 10);
        const giftVariantId = Number.parseInt(node.dataset.giftVariantId || '', 10);
        if (!thresholdCents || thresholdCents <= 0) return null;

        return {
          milestoneKey: node.dataset.milestoneKey?.trim() || '',
          giftVariantId: Number.isInteger(giftVariantId) && giftVariantId > 0 ? giftVariantId : null,
          thresholdCents,
          amountText: node.dataset.amountText || formatMoney(thresholdCents),
          label: node.dataset.label || 'Reward',
          headingPhrase: node.dataset.headingPhrase || 'reward',
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.thresholdCents - right.thresholdCents)
      .filter((item, index, items) => {
        return index === 0 || item.thresholdCents !== items[index - 1].thresholdCents;
      });
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error('Failed to load cart');
    }

    return response.json();
  }

  async function fetchMiniCartItemsHtml() {
    const response = await fetch(MINI_CART_VIEW_URL + '&_=' + Date.now(), {
      cache: 'no-store',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error('Failed to load mini cart markup');
    }

    return response.text();
  }

  async function updateCartItems(updates) {
    if (!updates || Object.keys(updates).length === 0) return;

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      throw new Error('Failed to update cart items');
    }

    return response.json();
  }

  async function addCartItems(items) {
    if (!items || items.length === 0) return;

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      throw new Error('Failed to add cart items');
    }

    return response.json();
  }

  function isThresholdGiftItem(item) {
    return item?.properties?.[GWP_PROPERTY] === 'true' || item?.properties?.[REWARD_MILESTONE_PROPERTY];
  }

  function getGiftItems(cart) {
    return (cart.items || []).filter((item) => item.properties?.[GWP_PROPERTY] === 'true');
  }

  function getRewardMilestoneItems(cart) {
    return (cart.items || []).filter((item) => {
      const milestoneKey = item.properties?.[REWARD_MILESTONE_PROPERTY];
      return typeof milestoneKey === 'string' && milestoneKey.trim() !== '';
    });
  }

  function getLineFinalPriceCents(item) {
    if (!item) return 0;

    if (typeof item.final_line_price === 'number') {
      return item.final_line_price;
    }

    if (typeof item.line_price === 'number') {
      return item.line_price;
    }

    const unitPrice =
      typeof item.final_price === 'number'
        ? item.final_price
        : typeof item.price === 'number'
          ? item.price
          : 0;
    const quantity = typeof item.quantity === 'number' ? item.quantity : 1;

    return unitPrice * quantity;
  }

  function getDisplaySubtotalCents(cart) {
    if (!cart) return 0;

    if (typeof cart.total_price === 'number') {
      return cart.total_price;
    }

    if (!Array.isArray(cart.items)) {
      return 0;
    }

    return cart.items.reduce((sum, item) => sum + getLineFinalPriceCents(item), 0);
  }

  function getThresholdSubtotalCents(cart) {
    const subtotal = getDisplaySubtotalCents(cart);
    const giftTotal = (cart?.items || [])
      .filter((item) => isThresholdGiftItem(item))
      .reduce((sum, item) => sum + getLineFinalPriceCents(item), 0);

    return Math.max(0, subtotal - giftTotal);
  }

  function getCartLineSignature(cart) {
    return (cart?.items || [])
      .map((item) => `${item.key}:${item.quantity}`)
      .sort()
      .join('|');
  }

  function shouldReloadCartPage(previousCart, nextCart) {
    const isCartPage = /(?:^|\/)cart\/?$/.test(window.location?.pathname || '');
    return isCartPage && getCartLineSignature(previousCart) !== getCartLineSignature(nextCart);
  }

  function getCurrentTier(cart, tiers) {
    const total = getThresholdSubtotalCents(cart);
    let currentTier = null;

    tiers.forEach((tier) => {
      if (total >= tier.thresholdCents) {
        currentTier = tier;
      }
    });

    return currentTier;
  }

  function getHighestTier(tiers) {
    return tiers.length > 0 ? tiers[tiers.length - 1] : null;
  }

  function getTierMessage(root, currentTier, highestTier) {
    if (!currentTier) {
      return root.dataset.miniCartGwpBeforeTier1 || '';
    }

    if (highestTier && currentTier.tier === highestTier.tier) {
      return root.dataset.miniCartGwpAtHighestTier || '';
    }

    if (currentTier.tier === 1) {
      return root.dataset.miniCartGwpAtTier1 || '';
    }

    if (currentTier.tier === 2) {
      return root.dataset.miniCartGwpAtTier2 || '';
    }

    return root.dataset.miniCartGwpAtHighestTier || '';
  }

  function renderRewardProgress(root, cart) {
    const rewardsRoot = root.querySelector('[data-mini-cart-rewards]');
    const heading = root.querySelector('[data-mini-cart-rewards-heading]');
    const fill = root.querySelector('[data-mini-cart-rewards-fill]');
    const steps = root.querySelector('[data-mini-cart-rewards-steps]');

    if (!rewardsRoot || !heading || !fill || !steps) return;

    const milestones = parseRewardMilestones(root);
    const itemCount = Number(cart?.item_count || 0);

    if (itemCount <= 0 || milestones.length === 0) {
      rewardsRoot.hidden = true;
      fill.style.width = '0%';
      heading.textContent = '';
      steps.innerHTML = '';
      return;
    }

    const total = getThresholdSubtotalCents(cart);
    const highestThreshold = milestones[milestones.length - 1].thresholdCents;
    let progressPercentage = 0;

    if (highestThreshold > 0) {
      progressPercentage = Math.min(100, Math.max(0, (total / highestThreshold) * 100));
    }

    const nextMilestone = milestones.find((milestone) => total < milestone.thresholdCents);

    rewardsRoot.hidden = false;
    fill.style.width = `${progressPercentage}%`;
    steps.innerHTML = milestones
      .map((milestone) => {
        const isActive = total >= milestone.thresholdCents ? ' is-active' : '';
        return `
          <div class="mini-cart-rewards-step${isActive}" data-mini-cart-reward-step>
            <span class="mini-cart-rewards-amount">${escapeHtml(milestone.amountText)}</span>
            <span class="mini-cart-rewards-dot"></span>
            <span class="mini-cart-rewards-label">${escapeHtml(milestone.label)}</span>
          </div>
        `;
      })
      .join('');

    if (nextMilestone) {
      const remaining = Math.max(0, nextMilestone.thresholdCents - total);
      heading.innerHTML = `You are <strong>${escapeHtml(formatMoney(remaining))}</strong> away from ${escapeHtml(nextMilestone.headingPhrase)}!`;
      return;
    }

    heading.textContent = root.dataset.miniCartRewardSuccessMessage || "You've unlocked all rewards!";
  }

  function buildSelectorMarkup(root, tiers, cart) {
    const container = root.querySelector(SELECTOR_CONTAINER);
    if (!container) return;

    const currentTier = getCurrentTier(cart, tiers);
    const highestTier = getHighestTier(tiers);

    if (!currentTier || currentTier.allowMultiple || currentTier.options.length < 2) {
      container.hidden = true;
      container.innerHTML = '';
      return;
    }

    const selectedVariantIds = new Set(
      getGiftItems(cart)
        .filter((item) => String(item.properties?.[GWP_TIER_PROPERTY] || '') === String(currentTier.tier))
        .map((item) => String(item.variant_id || ''))
    );
    const message = getTierMessage(root, currentTier, highestTier);

    container.hidden = false;
    container.innerHTML = `
      <div class="mini-cart-gwp-selector-badge">${escapeHtml(currentTier.label)}</div>
      ${
        message
          ? `<p class="mini-cart-gwp-selector-message">${escapeHtml(message)}</p>`
          : ''
      }
      <div class="mini-cart-gwp-selector-options">
        ${currentTier.options
          .map((option) => {
            const isSelected = selectedVariantIds.has(String(option.variantId));
            const optionStateClass = `${isSelected ? ' is-selected' : ''}${
              option.isDefault && !isSelected ? ' is-default' : ''
            }${option.isAvailable ? '' : ' is-disabled'}`;
            const buttonLabel = option.isAvailable
              ? isSelected
                ? 'Selected'
                : 'Choose'
              : 'Unavailable';

            return `
              <div class="mini-cart-gwp-selector-option${optionStateClass}">
                <div class="mini-cart-gwp-selector-image">
                  ${
                    option.imageUrl
                      ? `<img src="${escapeHtml(option.imageUrl)}" alt="${escapeHtml(option.title)}" loading="lazy">`
                      : ''
                  }
                </div>
                <div class="mini-cart-gwp-selector-copy">
                  <p class="mini-cart-gwp-selector-title">${escapeHtml(option.title)}</p>
                </div>
                <button
                  type="button"
                  class="mini-cart-gwp-selector-button${isSelected ? ' is-secondary' : ''}"
                  data-mini-cart-gwp-select
                  data-tier="${escapeHtml(currentTier.tier)}"
                  data-variant-id="${escapeHtml(option.variantId)}"
                  ${isSelected || !option.isAvailable ? 'disabled' : ''}
                >
                  ${buttonLabel}
                </button>
              </div>
            `;
          })
          .join('')}
      </div>
    `;
  }

  function updateMiniCartDom(root, cart, itemsHtml) {
    const list = root.querySelector('[data-mini-cart-form] ul');
    const itemsContainer = root.querySelector('[data-mini-cart-items]');
    const emptyContainer = root.querySelector('[data-mini-cart-empty]');
    const totalsContainer = root.querySelector('[data-mini-cart-totals]');
    const subtotal = root.querySelector('[data-mini-cart-subtotal]');
    const cartCountNodes = document.querySelectorAll('[data-cart-count]');
    const badgeNodes = document.querySelectorAll('[data-cart-count-badge]');

    if (list) {
      list.innerHTML = itemsHtml.trim();
    }

    if (subtotal) {
      subtotal.textContent = formatMoney(getDisplaySubtotalCents(cart));
    }

    cartCountNodes.forEach((node) => {
      node.textContent = String(cart.item_count || 0);
    });

    badgeNodes.forEach((node) => {
      node.hidden = (cart.item_count || 0) <= 0;
    });

    const isEmpty = (cart.item_count || 0) === 0;
    root.classList.toggle('is-empty', isEmpty);
    root.classList.toggle('panel-alt', !isEmpty);

    if (itemsContainer) itemsContainer.classList.toggle('hidden', isEmpty);
    if (emptyContainer) emptyContainer.classList.toggle('hidden', !isEmpty);
    if (totalsContainer) totalsContainer.classList.toggle('hidden', isEmpty);
  }

  function emitCartChanged(cart) {
    window.dispatchEvent(
      new CustomEvent('cart:changed', {
        detail: {
          cart,
          source: 'mini-cart-gwp',
        },
      })
    );
  }

  async function refreshMiniCart(root, cart) {
    const nextCart = cart || (await fetchCart());
    const itemsHtml = await fetchMiniCartItemsHtml();
    updateMiniCartDom(root, nextCart, itemsHtml);
    renderRewardProgress(root, nextCart);
    buildSelectorMarkup(root, parseConfig(root), nextCart);
    emitCartChanged(nextCart);
    return nextCart;
  }

  async function normalizeGifts(cart, currentTier) {
    const giftItems = getGiftItems(cart);
    if (giftItems.length === 0) return cart;

    const updates = {};

    if (!currentTier) {
      giftItems.forEach((item) => {
        updates[item.key] = 0;
      });
    } else if (currentTier.allowMultiple) {
      const allowedVariants = new Set(currentTier.options.map((option) => String(option.variantId)));
      const keptVariants = new Set();

      giftItems.forEach((item) => {
        const itemTier = String(item.properties?.[GWP_TIER_PROPERTY] || '');
        const variantId = String(item.variant_id || '');
        const isEligibleTier = itemTier === String(currentTier.tier);

        if (!isEligibleTier || !allowedVariants.has(variantId) || keptVariants.has(variantId)) {
          updates[item.key] = 0;
          return;
        }

        keptVariants.add(variantId);

        if (item.quantity > 1) {
          updates[item.key] = 1;
        }
      });
    } else {
      const allowedVariants = new Set(currentTier.options.map((option) => String(option.variantId)));
      let keptKey = null;

      giftItems.forEach((item) => {
        const itemTier = String(item.properties?.[GWP_TIER_PROPERTY] || '');
        const variantId = String(item.variant_id || '');
        const isEligibleTier = itemTier === String(currentTier.tier);

        if (!isEligibleTier || !allowedVariants.has(variantId) || keptKey) {
          updates[item.key] = 0;
          return;
        }

        keptKey = item.key;

        if (item.quantity > 1) {
          updates[item.key] = 1;
        }
      });
    }

    if (Object.keys(updates).length === 0) {
      return cart;
    }

    await updateCartItems(updates);
    return fetchCart();
  }

  async function normalizeRewardMilestoneGifts(cart, milestones) {
    const rewardItems = getRewardMilestoneItems(cart);
    if (rewardItems.length === 0) return cart;

    const thresholdSubtotal = getThresholdSubtotalCents(cart);
    const eligibleMilestone = milestones
      .filter(
        (milestone) =>
          milestone.thresholdCents <= thresholdSubtotal &&
          milestone.milestoneKey &&
          milestone.giftVariantId
      )
      .slice(-1)[0] || null;
    const eligibleKey = eligibleMilestone?.milestoneKey || '';
    const updates = {};
    let keptKey = '';

    rewardItems.forEach((item) => {
      const itemMilestoneKey = String(item.properties?.[REWARD_MILESTONE_PROPERTY] || '').trim();

      if (!eligibleKey || itemMilestoneKey !== eligibleKey || keptKey) {
        updates[item.key] = 0;
        return;
      }

      keptKey = item.key;

      if (item.quantity > 1) {
        updates[item.key] = 1;
      }
    });

    if (Object.keys(updates).length === 0) {
      return cart;
    }

    await updateCartItems(updates);
    return fetchCart();
  }

  async function autoAddDefaultGifts(cart, currentTier) {
    if (!currentTier) return cart;

    const giftItems = getGiftItems(cart).filter(
      (item) => String(item.properties?.[GWP_TIER_PROPERTY] || '') === String(currentTier.tier)
    );
    const selectedVariantIds = new Set(giftItems.map((item) => String(item.variant_id || '')));
    const defaultOptions = currentTier.options.filter(
      (option) => option.isAvailable && (option.isDefault || currentTier.options.length === 1)
    );

    if (defaultOptions.length === 0) {
      return cart;
    }

    const optionsToAdd = currentTier.allowMultiple
      ? defaultOptions.filter((option) => !selectedVariantIds.has(String(option.variantId)))
      : [defaultOptions[0]].filter((option) => !selectedVariantIds.has(String(option.variantId)));

    if (optionsToAdd.length === 0) {
      return cart;
    }

    await addCartItems(
      optionsToAdd.map((option) => ({
        id: option.variantId,
        quantity: 1,
        properties: {
          [GWP_PROPERTY]: 'true',
          [GWP_TIER_PROPERTY]: String(currentTier.tier),
        },
      }))
    );

    return fetchCart();
  }

  async function reconcileMiniCartGwp(reason) {
    const root = getMiniCartRoot();
    if (!root) return;
    // A campaign gift can change while the native cart is rendering.
    // Keep the refresh queued so its rows and totals cannot stay stale.
    if (isReconciling) {
      scheduleReconcile(reason, 180);
      return;
    }

    const tiers = parseConfig(root);
    const rewardMilestones = parseRewardMilestones(root);
    if (tiers.length === 0 && rewardMilestones.length === 0 && reason !== 'paid-seed-offer') return;

    isReconciling = true;
    const selector = root.querySelector(SELECTOR_CONTAINER);
    selector?.classList.add('is-loading');

    try {
      const initialCart = await fetchCart();
      let cart = initialCart;

      if (rewardMilestones.length > 0) {
        cart = await normalizeRewardMilestoneGifts(cart, rewardMilestones);
      }

      if (tiers.length > 0) {
        let currentTier = getCurrentTier(cart, tiers);
        cart = await normalizeGifts(cart, currentTier);
        currentTier = getCurrentTier(cart, tiers);
        cart = await autoAddDefaultGifts(cart, currentTier);
      }

      if (shouldReloadCartPage(initialCart, cart)) {
        window.location.reload();
        return;
      }

      await refreshMiniCart(root, cart);
    } catch (error) {
      console.error(`[mini-cart-gwp] reconcile failed (${reason})`, error);
    } finally {
      selector?.classList.remove('is-loading');
      isReconciling = false;
    }
  }

  function scheduleReconcile(reason, delay) {
    window.clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(() => {
      reconcileMiniCartGwp(reason);
    }, delay);
  }

  function setMiniCartTriggerLoading(trigger, isLoading) {
    const icon = trigger?.querySelector('[data-mini-cart-trigger-icon]');
    if (!icon) return;

    if (!icon.dataset.defaultIcon) {
      icon.dataset.defaultIcon = icon.innerHTML;
    }

    if (isLoading) {
      icon.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><circle fill="currentColor" stroke="currentColor" stroke-width="15" r="15" cx="40" cy="100"><animate attributeName="opacity" calcMode="spline" dur="2" values="1;0;1;" keySplines=".5 0 .5 1;.5 0 .5 1" repeatCount="indefinite" begin="-.4"></animate></circle><circle fill="currentColor" stroke="currentColor" stroke-width="15" r="15" cx="100" cy="100"><animate attributeName="opacity" calcMode="spline" dur="2" values="1;0;1;" keySplines=".5 0 .5 1;.5 0 .5 1" repeatCount="indefinite" begin="-.2"></animate></circle><circle fill="currentColor" stroke="currentColor" stroke-width="15" r="15" cx="160" cy="100"><animate attributeName="opacity" calcMode="spline" dur="2" values="1;0;1;" keySplines=".5 0 .5 1;.5 0 .5 1" repeatCount="indefinite" begin="0"></animate></circle></svg>';
      icon.classList.add('is-loading');
      trigger.setAttribute('aria-busy', 'true');
      return;
    }

    icon.innerHTML = icon.dataset.defaultIcon;
    icon.classList.remove('is-loading');
    trigger.removeAttribute('aria-busy');
  }

  async function handleMiniCartTriggerClick(event) {
    const trigger = event.target.closest('[data-panel-trigger="mini-cart"]');
    if (!trigger || allowNextMiniCartTrigger) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    setMiniCartTriggerLoading(trigger, true);

    try {
      await reconcileMiniCartGwp('pre-open');
      allowNextMiniCartTrigger = true;
      trigger.click();
    } finally {
      allowNextMiniCartTrigger = false;
      setMiniCartTriggerLoading(trigger, false);
    }
  }

  async function handleGiftSelection(event) {
    const button = event.target.closest('[data-mini-cart-gwp-select]');
    if (!button || button.disabled || isReconciling) return;

    event.preventDefault();

    const root = getMiniCartRoot();
    if (!root) return;

    const tiers = parseConfig(root);
    const tier = tiers.find((entry) => String(entry.tier) === String(button.dataset.tier || ''));
    if (!tier) return;

    const selector = root.querySelector(SELECTOR_CONTAINER);
    selector?.classList.add('is-loading');
    isReconciling = true;

    try {
      const cart = await fetchCart();
      const updates = {};

      getGiftItems(cart).forEach((item) => {
        updates[item.key] = 0;
      });

      if (Object.keys(updates).length > 0) {
        await updateCartItems(updates);
      }

      await addCartItems([
        {
          id: Number.parseInt(button.dataset.variantId || '', 10),
          quantity: 1,
          properties: {
            [GWP_PROPERTY]: 'true',
            [GWP_TIER_PROPERTY]: String(tier.tier),
          },
        },
      ]);

      const nextCart = await fetchCart();
      await refreshMiniCart(root, nextCart);
    } catch (error) {
      console.error('[mini-cart-gwp] gift selection failed', error);
    } finally {
      selector?.classList.remove('is-loading');
      isReconciling = false;
    }
  }

  function initMiniCartGwp() {
    const root = getMiniCartRoot();
    if (!root) return;

    if (!root.dataset.miniCartGwpBound) {
      root.addEventListener('click', handleGiftSelection);
      root.dataset.miniCartGwpBound = 'true';
    }

    scheduleReconcile('init', 40);
    document.addEventListener('click', handleMiniCartTriggerClick, true);
  }

  document.addEventListener('DOMContentLoaded', initMiniCartGwp);

  window.addEventListener('panel:opened', (event) => {
    if (event.detail?.panelId === 'mini-cart') {
      scheduleReconcile('panel-opened', 60);
    }
  });

  window.addEventListener('cart:changed', (event) => {
    if (event.detail?.source === 'mini-cart-gwp') return;
    scheduleReconcile(event.detail?.source === 'paid-seed-offer' ? 'paid-seed-offer' : 'cart-changed', 180);
  });
})();
